import { OAuthConnectionFieldsType } from '../../../api/configurations/oauth2connection/types';
import { validateHexColor, validateMobileDeepLink, validateUrlLink } from '../../../utils';
import { ExcludedValidationFieldsList } from './utils';

export type FormValues = {
  redirect_uris: string[];
  name: string;
  merchant_project_id: string;
  lifetime: number;
  audience: string[];
  scopes: string[];
};

export type OAuthConnectionFieldsTypeError = {
  scope: string[];
  [key: string]: number | string | boolean | undefined | string[];
};

export const CLIENT_NAME_MAX_LENGTH = 100;
export const CLIENT_NAME_MIN_LENGTH = 2;
export const CLIENT_ID_MAX_LENGTH = 255;
export const DISPLAY_NAME_MAX_LENGTH = 30;
export const IMAGE_MAX_FILE_SIZE = 307200;

const validateUris = (linkString: string) =>
  validateUrlLink(linkString) || validateMobileDeepLink(linkString);

export const Oauth2ConnectionValidator = (
  values: OAuthConnectionFieldsType
): OAuthConnectionFieldsTypeError => {
  const errors: OAuthConnectionFieldsTypeError = {
    scope: []
  };

  Object.entries(values).forEach(([key, value]) => {
    if (!ExcludedValidationFieldsList.includes(key)) {
      if (!value) {
        errors[key] =
          key === 'logo_url' ? 'app.validation.logo_url.required' : 'app.validation.required';
      }

      if ((key === 'display_name' ? /^\s|\s$/ : /\s/).test(value as string)) {
        errors[key] = 'app.validation.value-invalid';
      }
    }
  });

  if (values.scope.filter(Boolean).length) {
    const accum = Object.create(null);

    errors.scope = values.scope.map((fieldValue) => {
      const duplicateErr = accum[fieldValue] && 'app.validation.value-invalid';
      const spaceErr = /\s/.test(fieldValue) ? 'app.validation.value-invalid' : undefined;
      accum[fieldValue] = true;

      return duplicateErr || spaceErr;
    });
  }

  if (values.name_prefix) {
    if (values.name_prefix.length > CLIENT_NAME_MAX_LENGTH) {
      errors.name_prefix = 'app.validation.characters';
    } else if (values.name_prefix.length < CLIENT_NAME_MIN_LENGTH) {
      errors.name_prefix = 'app.validation.characters.min';
    } else if (!/^[a-zA-Z0-9]+(?:[-_][a-zA-Z0-9]+)*$/.test(values.name_prefix)) {
      errors.name_prefix = 'app.validation.characters-only-hyphen-underscore';
    }
  }

  if (!validateUris(values.url_access_token)) {
    errors.url_access_token = 'authorization.settings.oauth2.validation.invalid-url';
  }

  if (!validateUris(values.url_authorize)) {
    errors.url_authorize = 'authorization.settings.oauth2.validation.invalid-url';
  }

  if (!validateUris(values.url_resource_owner_details)) {
    errors.url_resource_owner_details = 'authorization.settings.oauth2.validation.invalid-url';
  }

  if (values.client_id && values.client_id.length > CLIENT_ID_MAX_LENGTH) {
    errors.client_id = 'app.validation.characters';
  }

  if (values.client_secret && values.client_secret.length > CLIENT_ID_MAX_LENGTH) {
    errors.client_secret = 'app.validation.characters';
  }

  if (values.display_name && values.display_name.length > DISPLAY_NAME_MAX_LENGTH) {
    errors.display_name = 'app.validation.characters';
  }

  if (values.background_color && !validateHexColor(values.background_color)) {
    errors.background_color = 'app.validation.color.hex';
  }

  return errors;
};
